import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useHistory } from 'react-router-dom';
import { ArrowDownUp, Bell, Briefcase, ChevronRight, Home, LocateFixed, MapPin, MessageCircle, Phone, Plane, QrCode, Route as RouteIcon, UserRound } from 'lucide-react';
import type { PlaceInput as Place } from '@tcrd/shared';
import { Avatar, Button, Card, SectionTitle, Skeleton } from '@tcrd/ui';
import { Page } from '../../components/Page';
import { PlaceInput } from '../../components/PlaceInput';
import { RequestCard, RideCard } from '../../components/TripCards';
import { api } from '../../lib/api';
import type { RideDto, RideRequestDto } from '../../lib/types';
import { useAuth } from '../../stores/auth';
import { useUnreadNotifications } from '../common/NotificationsPage';
import { DriverVehicleCard } from '../common/RideInfoCards';
import { useMyDriver } from './MyDriverPage';
import { DriverHeroCard } from '../../components/DriverHeroCard';

interface Address {
  id: string;
  label: string;
  address: string;
  lat: number;
  lng: number;
}

/** Aeropuerto principal (acceso rápido). */
const AIRPORT: Place = { address: 'Aeropuerto Internacional Las Américas (SDQ)', lat: 18.4297, lng: -69.6689 };

/** URL de "Solicitar viaje" con origen y/o destino precargados. */
export function requestUrl(to?: Place | null, from?: Place | null) {
  const q = new URLSearchParams();
  for (const [k, p] of [['from', from], ['to', to]] as const) {
    if (!p) continue;
    q.set(`${k}Lat`, String(p.lat));
    q.set(`${k}Lng`, String(p.lng));
    q.set(`${k}Addr`, p.address);
  }
  const s = q.toString();
  return `/app/request${s ? `?${s}` : ''}`;
}

const placeIcon = (label: string) => (/casa|hogar/i.test(label) ? Home : /trabajo|oficina/i.test(label) ? Briefcase : MapPin);

export function CustomerHome() {
  const history = useHistory();
  const user = useAuth((s) => s.user);
  const unread = useUnreadNotifications();
  const rides = useQuery({ queryKey: ['rides', 'upcoming'], queryFn: () => api<RideDto[]>('/rides', { query: { scope: 'upcoming' } }) });
  const past = useQuery({ queryKey: ['rides', 'past'], queryFn: () => api<RideDto[]>('/rides', { query: { scope: 'past' } }) });
  const requests = useQuery({ queryKey: ['ride-requests', 'open'], queryFn: () => api<RideRequestDto[]>('/ride-requests', { query: { status: 'REQUESTED,INFO_REQUESTED,QUOTED' } }) });
  const addresses = useQuery({ queryKey: ['addresses'], queryFn: () => api<Address[]>('/customers/me/addresses') });
  const myDriver = useMyDriver();
  const driver = myDriver.data?.driver;
  const active = rides.data?.find((r) => ['DRIVER_ON_THE_WAY', 'DRIVER_ARRIVED', 'IN_PROGRESS'].includes(r.status));
  const upcoming = (rides.data ?? []).filter((r) => r.status === 'CONFIRMED').slice(0, 3);

  // Viajes frecuentes: las rutas que más repite (de sus viajes completados).
  const frequent = useMemo(() => {
    const m = new Map<string, { origin: Place; destination: Place; count: number }>();
    for (const r of past.data ?? []) {
      if (r.status !== 'COMPLETED') continue;
      const k = `${r.origin.address}→${r.destination.address}`;
      const x = m.get(k) ?? { origin: r.origin, destination: r.destination, count: 0 };
      x.count++;
      m.set(k, x);
    }
    return [...m.values()].sort((a, b) => b.count - a.count).slice(0, 3);
  }, [past.data]);

  // Accesos rápidos: direcciones guardadas (Casa, Trabajo…), aeropuerto y "Otro".
  const quick = [...(addresses.data ?? []).slice(0, 2).map((a) => ({ key: a.id, label: a.label, icon: placeIcon(a.label), place: a as Place })), { key: 'airport', label: 'Aeropuerto', icon: Plane, place: AIRPORT }];
  const short = (s: string) => s.split(',')[0]!;

  return (
    <Page
      title="Inicio"
      logo="left"
      onRefresh={() => Promise.all([rides.refetch(), requests.refetch(), past.refetch(), myDriver.refetch()])}
      actions={
        <span className="flex items-center gap-1 pr-2">
          <Link to="/app/notifications" className="tap relative flex items-center justify-center px-2" aria-label="Notificaciones">
            <Bell className="size-6" />
            {!!unread.data?.count && <span className="absolute top-1 right-1 size-2.5 rounded-full bg-brand-red" aria-hidden />}
          </Link>
          <Link to="/app/profile" className="tap rounded-full" aria-label="Mi perfil">
            <Avatar src={user?.avatarUrl} firstName={user?.firstName} lastName={user?.lastName} size={36} />
          </Link>
        </span>
      }
    >
      <div className="flex flex-col gap-5">
        {/* Tu conductor: tarjeta con estado, puntuación, vehículo y accesos directos. */}
        {driver ? (
          <div className="flex flex-col gap-2">
            <DriverHeroCard d={driver} />
            <div className="grid grid-cols-3 gap-2">
              <a href={driver.phone ? `tel:${driver.phone}` : undefined} aria-disabled={!driver.phone} className="tap h-11 rounded-xl border border-[var(--border)] bg-[var(--surface)] flex items-center justify-center gap-2 text-sm font-medium aria-disabled:opacity-50">
                <Phone className="size-4 text-brand-red" aria-hidden /> Llamar
              </a>
              <Link to="/app/chat" className="tap h-11 rounded-xl border border-[var(--border)] bg-[var(--surface)] flex items-center justify-center gap-2 text-sm font-medium">
                <MessageCircle className="size-4 text-brand-red" aria-hidden /> Chat
              </Link>
              <Link to="/app/my-driver" className="tap h-11 rounded-xl border border-[var(--border)] bg-[var(--surface)] flex items-center justify-center gap-2 text-sm font-medium">
                <UserRound className="size-4 text-brand-red" aria-hidden /> Ver perfil
              </Link>
            </div>
          </div>
        ) : (
          myDriver.data && (
            <Link to="/app/my-driver">
              <Card className="p-4 flex items-center gap-3 border-2 border-brand-red/60">
                <QrCode className="size-8 text-brand-red shrink-0" aria-hidden />
                <div className="flex-1">
                  <p className="font-semibold">Únete a tu conductor</p>
                  <p className="text-sm text-[var(--text-muted)]">Escanea su QR o elige un conductor de confianza de la lista.</p>
                </div>
              </Card>
            </Link>
          )
        )}

        {active && (
          <section>
            <SectionTitle title="Viaje en curso" />
            <Link to={`/app/rides/${active.id}/live`}><DriverVehicleCard ride={active} /></Link>
          </section>
        )}

        {/* Solicitar un viaje: desde la ubicación actual hacia el destino o un acceso rápido. */}
        <Card className="p-4 flex flex-col gap-3">
          <h2 className="text-lg font-bold">Solicitar un viaje</h2>
          <div className="relative flex flex-col gap-2">
            <Link to="/app/request" className="tap flex items-center gap-3 rounded-2xl border border-[var(--border)] bg-[var(--surface-2)] px-4 h-14">
              <span className="text-xs text-[var(--text-muted)] w-10">Desde</span>
              <span className="flex-1 font-medium">Mi ubicación actual</span>
              <LocateFixed className="size-5 text-[var(--text-muted)]" aria-hidden />
            </Link>
            <PlaceInput label="Hacia" value={null} onChange={(p) => p && history.push(requestUrl(p))} dotClass="bg-brand-red" allowCurrentLocation={false} />
            <ArrowDownUp className="absolute right-4 top-[3.25rem] size-4 text-[var(--text-muted)] pointer-events-none" aria-hidden />
          </div>
          <div className="grid grid-cols-4 gap-2">
            {quick.map((q) => (
              <button key={q.key} type="button" onClick={() => history.push(requestUrl(q.place))} className="tap flex flex-col items-center gap-1 rounded-2xl border border-[var(--border)] bg-[var(--surface)] py-3 text-xs font-medium hover:border-brand-red">
                <q.icon className="size-5 text-brand-red" aria-hidden />
                <span className="truncate max-w-full px-1">{q.label}</span>
              </button>
            ))}
            <Link to="/app/request" className="tap flex flex-col items-center gap-1 rounded-2xl border border-[var(--border)] bg-[var(--surface)] py-3 text-xs font-medium">
              <MapPin className="size-5 text-brand-red" aria-hidden />
              Otro
            </Link>
          </div>
          <Link to="/app/request"><Button block size="lg" variant="accent">Solicitar viaje</Button></Link>
        </Card>

        {(rides.isLoading || requests.isLoading) && <Skeleton className="h-28" />}
        {!!requests.data?.length && (
          <section>
            <SectionTitle title="Solicitudes pendientes" />
            <div className="flex flex-col gap-3">{requests.data.slice(0, 3).map((r) => <RequestCard key={r.id} r={r} href={`/app/requests/${r.id}`} />)}</div>
          </section>
        )}
        {upcoming.length > 0 && (
          <section>
            <SectionTitle title="Próximos viajes" action={<Link to="/app/trips" className="text-sm font-medium text-brand-red">Ver todos</Link>} />
            <div className="flex flex-col gap-3">{upcoming.map((r) => <RideCard key={r.id} r={r} href={`/app/rides/${r.id}`} />)}</div>
          </section>
        )}
        {frequent.length > 0 && (
          <section>
            <SectionTitle title="Viajes frecuentes" action={<Link to="/app/recurring" className="text-sm font-medium text-brand-red">Recurrentes</Link>} />
            <Card className="divide-y divide-[var(--border)] overflow-hidden">
              {frequent.map((f) => (
                <button key={`${f.origin.address}${f.destination.address}`} type="button" onClick={() => history.push(requestUrl(f.destination, f.origin))} className="tap w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-black/[0.03] dark:hover:bg-white/5">
                  <span className="size-10 rounded-xl bg-brand-red/10 flex items-center justify-center shrink-0"><RouteIcon className="size-5 text-brand-red" aria-hidden /></span>
                  <span className="flex-1 min-w-0">
                    <span className="block font-medium truncate">{short(f.origin.address)} → {short(f.destination.address)}</span>
                    <span className="block text-xs text-[var(--text-muted)]">{f.count} viaje{f.count === 1 ? '' : 's'} · pedir de nuevo</span>
                  </span>
                  <ChevronRight className="size-5 text-[var(--text-muted)]" aria-hidden />
                </button>
              ))}
            </Card>
          </section>
        )}
      </div>
    </Page>
  );
}
